import { fromPairs, isEmpty, map } from 'lodash';
import { StateCreator } from 'zustand';

import { FeatureFlag } from './enums/FeatureFlag';
import { buildContextWithKey } from './functions/buildContextWithKey';
import { buildExposureKey } from './functions/buildExposureKey';
import { isCacheStale } from './functions/isCacheStale';
import { resolveFromCache } from './functions/resolveFromCache';
import { sanitizeContext } from './functions/sanitizeContext';
import { FeatureResponse } from './types/FeatureResponse';
import { FeatureState } from './types/FeatureState';
import { FeatureStoreDependencies } from './types/FeatureStoreDependencies';

export const featureStore =
  ({
    appName,
    featureURL,
    useApiStore,
  }: FeatureStoreDependencies): StateCreator<FeatureState> =>
  (set, get) => ({
    countedExposureKeys: new Set<string>(), // de-dupe exposures counted within the current metric reporting window
    defaultContext: {}, // global context merged into every fetch request
    featureFlags: {}, // cached feature flags per contextKey
    lastInteractedFlags: {}, // last recorded values per-flag
    pendingMetricEvals: new Map<string, Set<FeatureFlag>>(), // queue of flags waiting for a fetch to complete per contextKey
    pendingRequests: new Map<string, Promise<void>>(), // in-flight fetches de-duped per contextKey
    ttl: 60_000, // cache TTL (ms)
    usageMetrics: {}, // metrics for current reporting window
    usageMetricsInterval: 60_000, // metrics reporting window (ms)

    /**
     * Merge an extra field into the shared default context.
     * Useful for long-lived (global) context.
     */
    addDefaultContextField: (key: string, value: unknown) => {
      set((state) => ({
        defaultContext: { ...state.defaultContext, [key]: value },
      }));
    },

    /**
     * Build the merged context and a stable cache key for it.
     * - The key is stable (sorted keys/normalized) via buildContextWithKey.
     */
    deriveContext: (context: Record<string, unknown>) => {
      const { defaultContext } = get();

      const sanitizedDefault = sanitizeContext(defaultContext);
      const sanitizedOverride = sanitizeContext(context);

      const [mergedContext, contextKey] = buildContextWithKey(
        sanitizedDefault,
        sanitizedOverride,
      );

      const providedContextHasKeys = Object.keys(context).length > 0;
      const mergedContextHasKeys = Object.keys(mergedContext).length > 0;

      // Determine if context can be used for flag evaluation if:
      // - merged context has at least one key, OR
      // - caller provided no context at all (support contextless flags)
      const contextNotReady = !mergedContextHasKeys && providedContextHasKeys;

      return { contextKey, contextNotReady, mergedContext };
    },

    /**
     * Evaluate whether a feature flag is enabled in current cache.
     * - No fetch, no metrics, no side effects (useful in hooks).
     * - Missing cache or missing flag => false.
     */
    getCachedEnabled: (flag: FeatureFlag, context = {}) => {
      const { featureFlags, deriveContext } = get();
      const { contextKey } = deriveContext(context);
      const entry = featureFlags[contextKey];

      if (!entry) {
        return false;
      }

      // Note: Unleash frontend returns only enabled toggles; missing => disabled.
      return entry.flags[flag] ?? false;
    },

    /**
     * Evaluate whether a feature flag is enabled.
     * - Skips network/metrics if override context is incomplete.
     * - On cache miss: queue metric to be flushed after fetch completes, then return false.
     * - On cache hit: record exposure (deduped) and return resolved value.
     * - On stale cache: refresh in background but still use current cache for the decision.
     */
    isEnabled: (flag: FeatureFlag, context = {}) => {
      const {
        featureFlags,
        ttl,
        deriveContext,
        getCachedEnabled,
        loadFlagsForContext,
        recordExposureIfFirst,
        queuePendingEvaluation,
      } = get();

      const { contextKey, contextNotReady } = deriveContext(context);

      // If user attempted context but all fields were nil (nothing to evaluate yet),
      // don't fetch or record. just return current cached value.
      if (contextNotReady) {
        return getCachedEnabled(flag, context);
      }

      const cached = featureFlags[contextKey];

      // Cache miss: queue for later flush (when fetch completes), fetch and return false.
      if (!cached) {
        queuePendingEvaluation(contextKey, flag);

        loadFlagsForContext(context);

        return false;
      }

      // Cache stale: fetch flags in background
      if (isCacheStale(cached, ttl)) {
        loadFlagsForContext(context);
      }

      // Resolve from cache and record exposure once for this (contextKey, flag, value) within the window.
      const enabled = resolveFromCache(cached, flag);
      recordExposureIfFirst(contextKey, flag, enabled);

      return enabled;
    },

    /**
     * Fetch flags for context and write the cache.
     * - De-duped via pendingRequests per contextKey.
     * - After cache is written, flush any queued evaluations for this contextKey.
     */
    loadFlagsForContext: (context: Record<string, unknown>) => {
      const {
        pendingMetricEvals,
        pendingRequests,
        deriveContext,
        recordExposureIfFirst,
      } = get();
      const { contextKey, contextNotReady, mergedContext } =
        deriveContext(context);

      // Skip when context is provided but is not usable yet
      if (contextNotReady) {
        return Promise.resolve();
      }

      // De-dupe in-flight request.
      if (pendingRequests.has(contextKey)) {
        return pendingRequests.get(contextKey) as Promise<void>;
      }

      const fetchPromise = useApiStore
        .getState()
        .featureApi()
        .post<never, FeatureResponse>(featureURL, { context: mergedContext })
        .then(({ toggles }) => {
          const flags = fromPairs(
            map(toggles, ({ name, enabled }) => [name, enabled]),
          );

          set((state) => ({
            featureFlags: {
              ...state.featureFlags,
              [contextKey]: { flags, timestamp: Date.now() },
            },
          }));

          // Flush any queued exposures for this contextKey
          const pending = pendingMetricEvals.get(contextKey);
          if (pending && pending.size > 0) {
            for (const pendingFlag of pending) {
              const enabled = Boolean(flags[pendingFlag]); // missing => false

              recordExposureIfFirst(contextKey, pendingFlag, enabled);
            }

            // Clear the queue for this key
            set((state) => {
              const next = new Map(state.pendingMetricEvals);

              next.delete(contextKey);

              return { pendingMetricEvals: next };
            });
          }
        })
        .finally(() => {
          pendingRequests.delete(contextKey);
        });

      pendingRequests.set(contextKey, fetchPromise);

      return fetchPromise;
    },

    /**
     * Queue a flag for exposure recording once the cache for `contextKey` arrives.
     * Used when `isEnabled` is called before we have the cache.
     */
    queuePendingEvaluation: (contextKey: string, flag: FeatureFlag) => {
      set((state) => {
        const next = new Map(state.pendingMetricEvals);
        const setForKey = new Set(next.get(contextKey) ?? []);

        setForKey.add(flag);
        next.set(contextKey, setForKey);

        return { pendingMetricEvals: next };
      });
    },

    /**
     * Record a yes/no exposure exactly once per reporting window
     * for a specific (contextKey, flag, value).
     */
    recordExposureIfFirst: (
      contextKey: string,
      flag: FeatureFlag,
      enabled: boolean,
    ) => {
      const { countedExposureKeys } = get();
      const exposureKey = buildExposureKey(contextKey, flag, enabled);

      // De-dupe within current metrics window
      if (countedExposureKeys.has(exposureKey)) {
        return;
      }

      set((state) => {
        const nextSeen = new Set(state.countedExposureKeys);
        nextSeen.add(exposureKey);

        const current = state.usageMetrics[flag] ?? { no: 0, yes: 0 };

        const updated = enabled
          ? { no: current.no, yes: current.yes + 1 }
          : { no: current.no + 1, yes: current.yes };

        return {
          countedExposureKeys: nextSeen,
          lastInteractedFlags: {
            ...state.lastInteractedFlags,
            [flag]: enabled,
          },
          usageMetrics: { ...state.usageMetrics, [flag]: updated },
        };
      });
    },

    /**
     * Sends the aggregated metrics and resets counters/dedup for the next reporting window.
     */
    reportMetrics: () => {
      const { usageMetrics, usageMetricsInterval } = get();

      if (isEmpty(usageMetrics)) {
        return;
      }

      const stop = new Date();
      const start = new Date(stop.getTime() - usageMetricsInterval);

      const featureApi = useApiStore.getState().featureApi();
      return featureApi
        .post<never, void>(`${featureURL}/client/metrics`, {
          appName,
          bucket: { start, stop, toggles: usageMetrics },
        })
        .then(() =>
          set({
            countedExposureKeys: new Set<string>(),
            usageMetrics: {},
          }),
        )
        .catch((error) => {
          console.error(error);
        });
    },
  });
